import { CustomError } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import type { Opaque } from 'cadenza/utils/opaque';
import { ajax, apiUrl, createEventSource } from 'cadenza/api-client/api';

/**
 * Result id for endpoints whose work order produces a
 * net.disy.cadenza.workorder.web.dto.FileDownloadWorkOrderResult.
 * Can be passed into {@link getWorkOrderResultUrl} to get the download url for the file produced by
 * the work order.
 */
export const FILE_DOWNLOAD_RESULT_ID = 'file';

const MAX_PROGRESS_RECONNECT_COUNT = 5;

export type WorkOrderId = Opaque<'WorkOrderId', string>;

interface ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result> extends Promise<Result> {
  started: (event: (event: StartedEvent) => void) => ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result>;
  progress: (event: (event: ProgressEvent) => void) => ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result>;
}

export class WorkOrderCanceledError extends CustomError {

  constructor () {
    super('WorkOrder canceled');
  }

}

export function getWorkOrderResultUrl (workOrderId: WorkOrderId, resultId: string, inline?: boolean): string {
  let url = `/workOrder/${workOrderId}/results/${resultId}`;
  if (inline) {
    url += '?inline';
  }
  return apiUrl(url);
}

/**
 * Deletes the workOrder specified by the given id. If the workOrder is still running, it's cancelled.
 *
 * @param workOrderIds
 * @return - Promise that is resolved when the workOrder is cancelled.
 */
export function deleteWorkOrders (workOrderIds: WorkOrderId[]): Promise<void> {
  return ajax(`/workOrder;workOrderIds=${workOrderIds.join()}`, { type: 'DELETE' });
}

export function observeForResult <StartedEvent, ProgressEvent, Result> (
  workOrderId: string,
  resultEventName: string,
  customEventListeners?: { [key: string]: ((eventData: MessageEvent) => void) }
): ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result> {
  if (!workOrderId) {
    throw new Error(`Must supply a workOrderId to observe for result '${resultEventName}'`);
  }
  // The endpoint supports observing multiple workOrders at once. For simplicity we just observe one
  // here for now as the promise, listeners and users would else need to handle multiple calls.
  const eventSource = createEventSource(`/workOrder;workOrderIds=${workOrderId}/monitor`);
  const startedListeners: ((event: StartedEvent) => void)[] = [];
  const progressListeners: ((event: ProgressEvent) => void)[] = [];
  const promise = new Promise((resolve, reject) => {
    let reconnectTryCount = 0;
    let resultReceived = false;
    let isCanceled = false;

    for (const [ customEventName, customEventListener ] of Object.entries(customEventListeners ?? {})) {
      logger.debug(`Registering event listener for custom SSE type: ${customEventName}.`);
      eventSource.addEventListener(customEventName, customEventListener);
    }

    eventSource.addEventListener('workorder-started', event => {
      const startedData = JSON.parse(event.data);
      startedListeners.forEach(startedListener => startedListener(startedData));
    });

    eventSource.addEventListener('workorder-progress', event => {
      reconnectTryCount = 0;
      const progressData = JSON.parse(event.data);
      progressListeners.forEach(progressListener => progressListener(progressData));
    });

    eventSource.addEventListener(resultEventName, event => {
      resultReceived = true;
      resolve(JSON.parse(event.data));
    });

    eventSource.addEventListener('workorder-canceled', () => {
      resultReceived = false;
      isCanceled = true;
      eventSource.close();
      reject(new WorkOrderCanceledError());
    });

    eventSource.addEventListener('workorder-stopped', event => {
      eventSource.close();
      const eventData = JSON.parse(event.data);
      const errorCode = eventData.errorCode;
      if (errorCode) {
        const error: Error & { key?: number } = new Error();
        error.key = errorCode;
        reject(error);
      }
      if (!resultReceived) {
        reject(new Error('WorkOrder stopped without receiving a result.'));
      }
    });

    eventSource.onerror = function () {
      if (eventSource.readyState === EventSource.CLOSED) {
        if (isCanceled) {
          reject(new WorkOrderCanceledError());
        } else {
          reject(new Error('WorkOrder progress request failed'));
        }
      } else if (reconnectTryCount >= MAX_PROGRESS_RECONNECT_COUNT) {
        eventSource.close();
        reject(new Error('WorkOrder progress request max reconnect count reached'));
      } else {
        logger.warn('Trying to reconnect to a WorkOrder progress SSE source');
        reconnectTryCount++;
      }
    };
  });

  // @ts-ignore – we add missing properties below
  const workOrderPromise: ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result> = promise;
  workOrderPromise.started = startedListener => {
    startedListeners.push(startedListener);
    return workOrderPromise;
  };
  workOrderPromise.progress = progressListener => {
    progressListeners.push(progressListener);
    return workOrderPromise;
  };
  return workOrderPromise;
}
